import { useEffect } from 'react';

import type { MutableRefObject } from 'react';

// Page -1 is the cover; 0..pages are slides.
const COVER = -1;

export interface ViewerKeyboardState {
  page: number;
  pages: number;
  setPage: (page: number) => void;
  // Slide names in order, used to find chapter starts (e.g. Strengths).
  slideNames: Array<string | undefined>;
}

// Page index where each chapter begins: the cover, then the first slide of each
// distinct slide name in order.
export const getChapterStarts = (
  slideNames: Array<string | undefined>,
): number[] => {
  const starts = [COVER];
  slideNames.forEach((name, index) => {
    if (index === 0 || name !== slideNames[index - 1]) {
      starts.push(index);
    }
  });
  return starts;
};

export const getNextPage = (
  key: string,
  { page, pages, slideNames }: Omit<ViewerKeyboardState, 'setPage'>,
): number | null => {
  const chapters = getChapterStarts(slideNames);
  const currentChapter = chapters.reduce(
    (found, start, index) => (start <= page ? index : found),
    0,
  );

  let target: number | undefined;
  if (key === 'ArrowRight') target = page + 1;
  if (key === 'ArrowLeft') target = page - 1;
  if (key === 'ArrowDown') target = chapters[currentChapter + 1];
  if (key === 'ArrowUp') target = chapters[currentChapter - 1];

  if (target === undefined || target < COVER || target > pages) return null;
  return target;
};

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)
  );
};

// Arrow-key navigation for the survey viewer: left/right move between pages, up/down
// jump between chapters (cover, Strengths, Growth Opportunities). The viewer fills the
// ref while it is showing slides, and clears it otherwise, so the listener is inert on
// loading and recipient-management screens.
export const useViewerKeyboardNavigation = (
  stateRef: MutableRefObject<ViewerKeyboardState | null>,
) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const state = stateRef.current;
      if (
        !state ||
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        isTypingTarget(event.target)
      ) {
        return;
      }

      const target = getNextPage(event.key, state);
      if (target === null) return;

      event.preventDefault();
      // After clicking an arrow button it keeps focus, and the first arrow key press
      // would make its keyboard-focus highlight appear. Drop focus so the keys drive
      // the viewer without leaving a highlighted button behind.
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        active.closest('[data-viewer-nav]')
      ) {
        active.blur();
      }
      state.setPage(target);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [stateRef]);
};
